import { Box, Card, Chip, Stack, Typography } from '@mui/material'
import { useState } from 'react'
import { ICapitalGood } from '../../../../shared/services/capital-good/capital-good.model'
import { monogramInitials, monogramTone } from '../../../../shared/utils/monogram-utils'

// Catalogue card for the Products page: compact and information-first, unlike the tall
// centred picker card the add-data flows use. The uploaded image (or a deterministic
// monogram tile) sits left; the footprint per unit is the headline datum.
// showSoldMarker: the sold tag only earns its place where the list mixes both kinds
// (All / Purchased views); on the Sold filter every card would repeat it.
// Shared with the catalogue's table view, so both render the same footprint wording.
export const productFootprintLabel = (good: ICapitalGood): string | null => {
  if (good.kgCarbonEmission == null) return null
  const value = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(good.kgCarbonEmission)
  const unitName = good.measurementUnit?.name || 'unit'
  const per = good.unitAmount && good.unitAmount !== 1 ? `${good.unitAmount} ${unitName}` : unitName
  return `${value} kgCO₂e / ${per}`
}

// selected / footprintLabel: the emissions catalogue picker reuses this card; selection mirrors
// the picker-card highlight, and the label override lets the Cat 11 catalogue lead with the
// stage figure that will actually book rather than the cradle-to-gate headline.
export const ProductCatalogueCard: React.FC<{
  good: ICapitalGood,
  showSoldMarker?: boolean,
  selected?: boolean,
  footprintLabel?: string | null,
}> = ({ good, showSoldMarker = true, selected = false, footprintLabel }) => {
  const [hover, setHover] = useState(false)
  const [imageError, setImageError] = useState(false)
  const tone = monogramTone(good.name)

  const footprint = () => footprintLabel !== undefined ? footprintLabel : productFootprintLabel(good)

  return (
    <Card
      variant='outlined'
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sx={{
        boxShadow: 'none',
        borderRadius: '10px',
        borderColor: hover || selected ? 'primary.main' : 'lightGrey.main',
        backgroundColor: selected ? 'secondary.light' : 'common.white',
        p: 2.5,
      }}>
      <Stack direction='row' spacing={2} alignItems='flex-start'>
        {good.capitalGoodImageFilePath && !imageError
          ? <img
            src={good.capitalGoodImageFilePath}
            alt={good.name}
            style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '10px', flexShrink: 0 }}
            onError={() => setImageError(true)}
          />
          : <Box sx={{ width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '10px', backgroundColor: tone.background, flexShrink: 0 }}>
            <Typography sx={{ color: tone.color, fontWeight: 600, fontSize: '1.1rem', letterSpacing: '0.02em' }}>
              {monogramInitials(good.name)}
            </Typography>
          </Box>}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='subtitle1' noWrap sx={{ color: 'text.primary', fontWeight: 600 }}>
            {good.name}
          </Typography>
          <Typography variant='body2' noWrap sx={{ color: 'text.secondary' }}>
            {[good.manufacturer?.name, good.modelNumber].filter(Boolean).join(' · ') || ' '}
          </Typography>
        </Box>
      </Stack>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mt: 2 }}>
        {footprint()
          ? <Typography variant='body2' sx={{ color: 'text.primary', fontWeight: 600 }}>{footprint()}</Typography>
          : <Typography variant='body2' sx={{ color: 'text.secondary' }}>No footprint yet</Typography>}
        {good.typeOfGood === 'soldProduct' && showSoldMarker &&
          <Chip size='small' label='Sold product' sx={{ backgroundColor: 'secondary.light', color: 'text.primary', fontWeight: 500 }} />}
      </Stack>
    </Card>
  )
}
